import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAssistant } from "@/features/tutor/context/AssistantContext.jsx";
import { useCooldown } from "@/features/tutor/context/CooldownContext";
import { useUIStore } from "@/features/tutor/store/useUIStore.js";
import { useSessionStore } from "@/features/tutor/store/useSessionStore.js";
import { useAudioStore } from "@/features/tutor/store/useAudioStore.js";
import { useTurnStore } from "@/features/tutor/store/useTurnStore.js";
import { useMicrophone } from "@/features/tutor/hooks/useMicrophone.js";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext.tsx";
import { useWebsocketClient } from "@/features/tutor/hooks/useWebsocketClient.js";
import { useTutorActionQueue } from "@/features/tutor/hooks/useTutorActionQueue.js";
import useLogger from "@/features/tutor/hooks/useLogger.js";
import Screen from "@/features/tutor/components/Screen.jsx";
import useAudioPlayer from "@/features/tutor/hooks/useAudioPlayer.js";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useCaptionsQueue } from "@/features/tutor/hooks/useCaptionsQueue";
import { CanvasCaptionsBridge } from "@/features/notepad/components/CanvasCaptionsBridge";
import useHandleTutorAction from "@/features/tutor/hooks/useHandleTutorAction.js";
import { useNextSlideHtmlPrefetch } from "@/features/tutor/hooks/useNextSlideHtmlPrefetch";
import { useAnnotationStore } from "@/features/tutor/store/useAnnotationStore.js";
import { useInactivityTimer } from "@/features/tutor/hooks/useInactivityTimer.js";
import { useNotificationStore } from "@/store/useNotificationStore";
import { useAuthStore } from "@/store/useAuthStore";
import { useDebugStore } from "@/store/useDebugStore";
import { useStudentInputStore } from "@/features/tutor/store/useStudentInputStore";
import AssessmentModal from "@/features/tutor/components/AssessmentModal.jsx";
import AssessmentSurvey from "@/features/tutor/components/AssessmentSurvey.jsx";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore.js";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes.js";
import { useBiteStatusStore } from "@/features/tutor/store/useBiteStatusStore";
import {
  captureLessonEndGuard,
  exitLesson,
  handleLessonEnd,
  openLessonCompleteModal,
  skipToExitTicket,
} from "@/features/tutor/utils/endLesson";
import { getSlideIndexAfterAssessment } from "@/features/tutor/utils/assessmentHelpers";
import { stopLessonSession } from "@/features/tutor/utils/stopLessonSession";
import { captureAndUploadSlideScreenshot } from "@/features/tutor/utils/slideScreenshot";
import { preloadCelebrationSounds } from "@/features/tutor/utils/celebrationSounds";
import { preloadCelebrationConfetti } from "@/features/tutor/utils/celebrationConfetti";
import { removeHoistedSlideStyles } from "@/features/tutor/vo-demo/utils/hoistSlideStylesheets";
import { clearCaptureImageCache } from "@/features/tutor/utils/slideCaptureImages";
import ExitLessonModal from "@/features/tutor/components/ExitLessonModal";
import LessonCompleteModal from "@/features/tutor/components/LessonCompleteModal";
import { everyBiteCompleted } from "@/features/tutor/utils/lessonCompletion";
import { usePracticeNotice } from "@/features/tutor/hooks/usePracticeNotice";
import { withMovedToFinished } from "@/utils/movedToFinished";
import { TUTOR_SESSION_PHASE_STATUS } from "@/features/tutor/constants";
import BottomBar from "@/features/tutor/components/buttons/BottomBar.jsx";
import MicSettingsGearButton from "@/features/tutor/components/buttons/MicSettingsGearButton";
import { useViewport } from "@/hooks/useViewport";
import { useSpeakButton } from "@/features/tutor/hooks/useSpeakButton.js";
import { CustomKeyboard } from "@/features/tutor/components/CustomKeyboard";
import {
  trackStudentInteraction,
  StudentInteractionTypes,
} from "../utils/mixpanel-utils";
import { TutorMode } from "@/constants/mode";
import { useForm } from "@/features/tutor/context/FormContext.tsx";
import { useConnectionStatus } from "@/features/tutor/hooks/useConnectionStatus";
import { DisconnectedOverlay } from "@/features/tutor/components/connection";
import SystemToasts from "@/features/tutor/components/connection/SystemToasts";
import { useMicPermissionStore } from "@/features/tutor/store/useMicPermissionStore";
import Captions from "@/features/tutor/components/Captions.jsx";
import { AnimatePresence } from "framer-motion";
import ResumeDebugPanel from "@/components/debug/ResumeDebugPanel";
import { DebugMenu } from "@/components/DebugMenu";
import {
  addResumeDebugEvent,
  useResumeDebugStore,
} from "@/store/useResumeDebugStore";
import { CANVAS_GATEWAY_CONFIGURED, SHOW_IMAGE_CAPTURE_DEBUG } from "@/flags";
import { usePaperCamera } from "@/features/tutor/components/PaperCamera/usePaperCamera";
import AddImageModal from "@/features/tutor/components/PaperCamera/AddImageModal";
import { NotepadCloseConfirmModal } from "@/features/notepad/components/NotepadCloseConfirmModal";
import {
  useNotepadStore,
  selectIsNotepadOpen,
} from "@/features/notepad/store/useNotepadStore";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import {
  clearPageUnloading,
  markPageUnloading,
} from "@/features/tutor/store/useConnectionStore";
import { restorePagesFromSession } from "@/features/notepad/utils/restorePagesFromSession";
import {
  CanvasChunkType,
  makeCanvasChunk,
} from "@/canvas/agent/chunk_protocol";
import { compress } from "@/utils/compression";
import { debugLog } from "@/utils/debugLog";
import {
  clearLessonStartupPerfAnchor,
  clearSessionStartupPerfAnchor,
  recordLessonStartupPerf,
  resolveLessonStartupPerfVariant,
} from "@/features/tutor/utils/lessonStartupPerf";
import {
  hasResumeDebugParam,
  resolveResumeDebugEnabled,
} from "@/features/tutor/vo-demo/utils/resumeDebug";
import { useAssessmentRuntime } from "@/features/tutor/vo-demo/runtime/useAssessmentRuntime";
import { useCanvasRuntimeBridge } from "@/features/tutor/vo-demo/runtime/useCanvasRuntimeBridge";
import { useInputPositionTracker } from "@/features/tutor/vo-demo/runtime/useInputPositionTracker";
import { useLessonAnalytics } from "@/features/tutor/vo-demo/runtime/useLessonAnalytics";
import { useResumePlaybackHold } from "@/features/tutor/vo-demo/runtime/useResumePlaybackHold";
import { useResumeRehydration } from "@/features/tutor/vo-demo/runtime/useResumeRehydration";
import { useVoDemoRuntimeEvents } from "@/features/tutor/vo-demo/runtime/useVoDemoRuntimeEvents";
import { useVoDemoSessionBoot } from "@/features/tutor/vo-demo/runtime/useVoDemoSessionBoot";
import { useLessonLeaveGuard } from "@/features/tutor/vo-demo/runtime/useLessonLeaveGuard";
import { useLockHorizontalOverscroll } from "@/features/tutor/hooks/useLockHorizontalOverscroll";
import { useLatestRef } from "@/features/tutor/vo-demo/runtime/useLatestRef";
import { useTutorAudioGain } from "@/features/tutor/hooks/useTutorAudioGain";
import { useCaptionsAvailabilitySync } from "@/features/tutor/hooks/useCaptionsAvailabilitySync";
import { useChatAvailabilitySync } from "@/features/tutor/hooks/useChatAvailabilitySync";
import { useChatTrailSync } from "@/features/tutor/hooks/useChatTrailSync";
import { useSendChatMessage } from "@/features/tutor/hooks/useSendChatMessage";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useScratchpadChipStore } from "@/features/tutor/store/useScratchpadChipStore";
import { useChatTrailLifetime } from "@/features/tutor/hooks/useChatTrailLifetime";
import { useInputModeReporter } from "@/features/tutor/hooks/useInputModeReporter";
import { useTutorAudioReporter } from "@/features/tutor/hooks/useTutorAudioReporter";
import { provideDevCommand } from "@/devHooks/devHookConnector";

function VoDemo({
  lessonId,
  boardSessionId,
  onSessionEstablished = () => {},
  mode = TutorMode.PUBLIC,
  shouldInitializeSession = true,
  sessionStartNonce = 0,
  pendingRehydratePayload = null,
  shouldHoldResumePlayback = true,
  startChoice = null,
  restartRequestedRef = null,
  onRestartCleanup = () => {},
  startupAuxMetadataReady = false,
  // Raised by the boot when it applies the aux block itself. Defaulted because
  // SlideEditor renders this component with nothing but a lesson id.
  setStartupAuxMetadataReady = () => {},
  cachedBitePromptState = { status: "inactive" },
  onCachedBiteChoice = () => {},
  onCachedBiteAttention = () => {},
  onRehydrateApplied = () => {},
  onSessionBootComplete = () => {},
  onSessionBootFailed = null,
  onSessionBootStarted = () => {},
}) {
  const { showDisconnectedOverlay } = useConnectionStatus();
  const { micPermissionDenied, micUnavailable } = useMicPermissionStore();
  const navigate = useNavigate();
  const { setTurnId, lockResumeTurnChanges, clearResumeTurnLock } =
    useTurnStore();
  const { formData } = useForm();
  const {
    lessonId: urlLessonId,
    sessionId: urlSessionId,
    model: urlModel,
    voiceModel: urlVoiceModel,
  } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const ltiLaunchRequestId = searchParams.get("ltiLaunchRequestId");
  const tvOverrideRef = useRef(
    import.meta.env.DEV || import.meta.env.VITE_ALLOW_TV_OVERRIDE === "true"
      ? new URLSearchParams(window.location.search).get("tv")
      : null,
  );
  const debugEnabled = useMemo(
    () =>
      resolveResumeDebugEnabled({
        searchParams,
        isDev: import.meta.env.DEV,
        showPanelEnv: import.meta.env.VITE_SHOW_RESUME_DEBUG_PANEL,
        localStorageValue:
          typeof window !== "undefined"
            ? window.localStorage.getItem("rrdebug")
            : null,
      }),
    [searchParams],
  );
  const setResumeDebugEnabled = useResumeDebugStore(
    (state) => state.setEnabled,
  );
  const wsConnected = useWebsocketStore((state) => state.wsConnected);
  const {
    handleChangeSlide,
    setCurrentSlideIndex,
    setCurrentLessonId,
    clearCurrentSlideIndex,
    clearSlides,
  } = useSlideStore();
  const { fetchSlidesByLesson } = useSlideStore();
  const setLessonId = useSlideStore((state) => state.setCurrentLessonId);
  const setCurrentLessonName = useSlideStore(
    (state) => state.setCurrentLessonName,
  );
  const currentLessonId = useSlideStore((state) => state.currentLessonId);
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const slideActionCount = useSlideStore((state) => state.slideActionCount);
  const { clearQueue, pauseTutorCaptions, resumeTutorCaptions } =
    useCaptionsQueue();
  const slides = useSlideStore((state) => state.slides);
  const getSlideByIndex = useSlideStore((state) => state.getSlideByIndex);
  const studentInputs = useStudentInputStore((state) => state.inputs);
  const markInputSubmitted = useStudentInputStore(
    (state) => state.markInputSubmitted,
  );
  const markInputRevealed = useStudentInputStore(
    (state) => state.markInputRevealed,
  );
  const setStudentInput = useStudentInputStore((state) => state.setInput);
  const getInputKeyByActionId = useStudentInputStore(
    (state) => state.getInputKeyByActionId,
  );
  const activeInputId = useStudentInputStore((state) => state.activeInputId);
  const [hasScratchpadPending, setHasScratchpadPending] = useState(false);
  const [hasCanvasViewportChanged, setHasCanvasViewportChanged] =
    useState(false);
  const scratchpadSubmitRef = useRef(null);
  const scratchpadResetPendingRef = useRef(null);
  const scratchpadCanvasExportRef = useRef(null);
  const scratchpadImageItemsRef = useRef(null);
  const scratchpadClearImageBytesRef = useRef(null);
  const scratchpadRemoveImageByIdRef = useRef(null);
  const pendingUploadedImageIdsByTurnRef = useRef(new Map());
  const inputPositions = useInputPositionTracker({
    studentInputs,
    currentSlideIndex,
  });
  const cachedBitePromptActive =
    cachedBitePromptState?.status === "announcing" ||
    cachedBitePromptState?.status === "prompting" ||
    cachedBitePromptState?.status === "applying";
  const {
    startInactivityTimer,
    endInactivityTimer,
    resetAndRestartInactivityTimer,
  } = useInactivityTimer({ suppressInactivity: cachedBitePromptActive });

  // Preload celebration sounds and confetti on mount so they start instantly
  // later; the slides' hoisted styles and cached capture images leave with the
  // lesson.
  useEffect(() => {
    preloadCelebrationSounds();
    preloadCelebrationConfetti();
    return () => {
      removeHoistedSlideStyles();
      clearCaptureImageCache();
    };
  }, []);

  // Reset and restart inactivity timer when student types in any input box
  useEffect(() => {
    // Track previous inputs to detect changes
    let prevInputs = useStudentInputStore.getState().inputs;

    // Subscribe to all state changes and check if inputs changed
    const unsubscribe = useStudentInputStore.subscribe((state) => {
      // Check if inputs object reference changed (happens on every input change)
      if (state.inputs !== prevInputs) {
        // Check if this is a real user input (new state has actual data)
        // Don't restart timer on clearInputs() which sets inputs to {}
        const isUserInput = Object.keys(state.inputs).length > 0;
        prevInputs = state.inputs;

        // Only restart timer if this is actual user input (not clearInputs initialization)
        // The hook will check if AI is speaking and prevent restart if needed
        if (isUserInput) {
          resetAndRestartInactivityTimer();
        }
      }
    });
    return unsubscribe;
  }, [resetAndRestartInactivityTimer]);
  const { setTutorSessionStatus } = useAssistant();
  const { initializeCompletionData } = useCooldown();
  const {
    showKeyboardGuide,
    isScratchPadOpen,
    canvasAccess,
    activeAssessmentModal,
    setIsScratchPadOpen,
    setIsScratchPadAvailable,
    setIsCalculatorAvailable,
    setCurrentCaptions,
    setCaptionsFrozen,
    setActiveAssessmentModal,
    setCanvasAccess,
    setIsNamePronunciationModalOpen,
  } = useUIStore();
  const showExitLessonModal = useUIStore((s) => s.showExitLessonModal);
  const setShowExitLessonModal = useUIStore((s) => s.setShowExitLessonModal);
  const showLessonCompleteModal = useUIStore((s) => s.showLessonCompleteModal);
  const lessonCompleteVariant = useUIStore((s) => s.lessonCompleteVariant);
  const closeLessonComplete = useUIStore((s) => s.closeLessonComplete);
  // Only No expectation's Incomplete tab sets this entry context. The list
  // verifies the server's completion status before showing its notice.
  const movedToFinished = useMemo(
    () =>
      showLessonCompleteModal &&
      !ltiLaunchRequestId &&
      searchParams.get("fromTab") === "incomplete" &&
      everyBiteCompleted(),
    [showLessonCompleteModal, ltiLaunchRequestId, searchParams],
  );
  const assessments = useAssessmentStore((s) => s.assessments);
  // pre_assessment is excluded here: VoDemoContainer owns its survey lifecycle.
  // Including it would cause VoDemo to mount a second AssessmentSurvey on top of
  // VoDemoContainer's, resulting in two overlapping surveys during a retake.
  const activeType =
    ASSESSMENT_KEYS.find(
      (k) => k !== "pre_assessment" && assessments[k].isActive,
    ) ?? null;
  const currentSessionId = useMemo(() => {
    return urlSessionId || searchParams.get("sessionId");
  }, [urlSessionId, searchParams]);

  // Discovery fixes board identity before the URL transition can replace the editor.
  const canvasSessionId =
    boardSessionId === undefined ? currentSessionId : boardSessionId;

  const hydrateChatSettings = useChatSettingsStore((s) => s.hydrate);
  useLayoutEffect(() => {
    if (!currentSessionId) return;
    hydrateChatSettings(currentSessionId);
    return () => useChatSettingsStore.getState().abandonSession();
  }, [currentSessionId, hydrateChatSettings]);
  useTutorAudioGain();
  useCaptionsAvailabilitySync();
  useChatAvailabilitySync();
  useChatTrailLifetime(currentSessionId);
  useInputModeReporter();
  useTutorAudioReporter();

  const currentLessonName = useMemo(() => {
    return searchParams.get("lessonName");
  }, [searchParams]);
  const [startupFirstAudioReceived, setStartupFirstAudioReceived] =
    useState(false);
  const [sessionBootComplete, setSessionBootComplete] = useState(false);
  const startupNonCriticalGate = useMemo(() => {
    // Keep prefetch/analytics off the critical boot path. Normal tutor starts
    // open this gate on first audio; no-audio startup paths fall back only after
    // session boot completes, so they still do not compete with start_session_lesson.
    if (!shouldInitializeSession) {
      return { ready: true, reason: "not_initializing" };
    }
    if (startupFirstAudioReceived) {
      return { ready: true, reason: "first_audio" };
    }
    if (sessionBootComplete) {
      return { ready: true, reason: "boot_complete_without_first_audio" };
    }
    return { ready: false };
  }, [sessionBootComplete, shouldInitializeSession, startupFirstAudioReceived]);
  const startupNonCriticalReady = startupNonCriticalGate.ready;
  const startupNonCriticalGateReason = startupNonCriticalGate.ready
    ? startupNonCriticalGate.reason
    : undefined;

  const canvasAccessLessonId = useMemo(
    () => currentLessonId || lessonId || urlLessonId,
    [currentLessonId, lessonId, urlLessonId],
  );

  useEffect(() => {
    setResumeDebugEnabled(debugEnabled);
    if (debugEnabled && hasResumeDebugParam(searchParams)) {
      window.localStorage.setItem("rrdebug", "1");
    }
    if (debugEnabled) {
      addResumeDebugEvent("debug:enabled", {
        lessonId,
      });
    }
  }, [debugEnabled, lessonId, searchParams, setResumeDebugEnabled]);

  const { setSessionId, currentScreen, setCurrentScreen, getSessionId } =
    useSessionStore();
  const { bufferingStatus, setIsAITalking, isAITalking } = useAudioStore();

  const {
    subscribe,
    emit,
    resetClock,
    doneChunkRecievedRef,
    abruptPauseTimerRef,
    isPausedRef,
    startTimeRef,
  } = useRuntimeContext();

  const { handleTutorAction } = useHandleTutorAction({
    mode,
    lessonId: lessonId || urlLessonId,
  });

  useEffect(() => {
    setStartupFirstAudioReceived(false);
    setSessionBootComplete(false);
  }, [currentLessonId, currentSessionId, sessionStartNonce]);

  useEffect(() => {
    if (!shouldInitializeSession || startupFirstAudioReceived) {
      return;
    }

    return subscribe("AUDIO_CHUNK", () => {
      setStartupFirstAudioReceived(true);
      recordLessonStartupPerf("startup_noncritical_gate_open", {
        lesson_id: currentLessonId,
        session_id: currentSessionId,
        gate: "first_audio",
      });
    });
  }, [
    currentLessonId,
    currentSessionId,
    shouldInitializeSession,
    startupFirstAudioReceived,
    subscribe,
  ]);

  const handleSessionBootCompleteForStartup = useCallback(() => {
    setSessionBootComplete(true);
    onSessionBootComplete();
  }, [onSessionBootComplete]);

  useEffect(() => {
    window.handleTutorAction = handleTutorAction;
  }, [handleTutorAction]);

  const { armResumePlaybackHold, releaseResumePlaybackHold } =
    useResumePlaybackHold({ pauseTutorCaptions, resumeTutorCaptions });

  const { clearResumeRuntimeState, resumeFragmentFloorRef } =
    useResumeRehydration({
      armResumePlaybackHold,
      clearResumeTurnLock,
      currentSlideIndex,
      getInputKeyByActionId,
      getSlideByIndex,
      lockResumeTurnChanges,
      markInputRevealed,
      onRehydrateApplied,
      pendingRehydratePayload,
      releaseResumePlaybackHold,
      setCurrentSlideIndex,
      setStudentInput,
      shouldHoldResumePlayback,
      shouldInitializeSession,
    });

  // Canvas open/close state — derived from NotepadStore
  const isCanvasOpen = useNotepadStore(selectIsNotepadOpen);
  const notepadState = useNotepadStore((s) => s.state);
  const notepadIngressType = useNotepadStore((s) => s.ingressType);
  const { isMobileLandscape, isMobile } = useViewport();
  const isMobileAny = isMobile || isMobileLandscape;
  const notepadUsedThisSlide = useNotepadPerSlideStore(
    (s) => s.flags[currentSlideIndex] === true,
  );
  const currentPageIndex = useNotepadStore((s) => s.currentPageIndex);
  const [showNotepadCloseConfirm, setShowNotepadCloseConfirm] = useState(false);
  const setIsAwaitingTutorResponseRef = useRef(() => {});
  const {
    canvasHistoryGetterRef,
    handleCanvasActionExecuted,
    handleCanvasError,
    handleCanvasStreamComplete,
    handleRegisterCanvasHistoryGetter,
    isCanvasAvailable,
    isCanvasOpenRef,
  } = useCanvasRuntimeBridge({
    canvasAccess,
    canvasAccessLessonId,
    clearCanvasCaptionsQueue: clearQueue,
    currentPageIndex,
    currentSlideIndex,
    doneChunkRecievedRef,
    emit,
    endInactivityTimer,
    getSlideByIndex,
    isCanvasOpen,
    isMobileAny,
    mode,
    navigate,
    searchParams,
    setActiveAssessmentModal,
    setCanvasAccess,
    setIsAwaitingTutorResponseRef,
    setIsCalculatorAvailable,
    setIsScratchPadAvailable,
    setIsScratchPadOpen,
    setSearchParams,
    slides,
    subscribe,
  });

  const extraListeningTimeoutHolderRef = useRef(null);
  const clearExtraListeningTimeout = useCallback(() => {
    const timeoutRef = extraListeningTimeoutHolderRef.current;
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const unmountCleanupRef = useLatestRef({
    canvasHistoryGetterRef,
    clearCurrentSlideIndex,
    clearExtraListeningTimeout,
    clearResumeRuntimeState,
    clearSlides,
    getSessionId,
    setCurrentLessonId,
    setCurrentLessonName,
    setLessonId,
    setSessionId,
  });

  useLockHorizontalOverscroll();
  useLessonLeaveGuard();

  useEffect(() => {
    // pagehide also covers iPadOS/Safari, and is the last reliable chance to
    // flush canvas chat history before the socket closes.
    const handlePageHide = () => {
      markPageUnloading();
      stopLessonSession();
      if (!CANVAS_GATEWAY_CONFIGURED) return;
      const getter = unmountCleanupRef.current.canvasHistoryGetterRef.current;
      if (!getter) return;
      const history = getter();
      if (!history || history.length === 0) return;
      const canvasWs = useNotepadStore.getState().notepadWs;
      if (!canvasWs || canvasWs.readyState !== WebSocket.OPEN) return;
      const chunk = makeCanvasChunk(
        CanvasChunkType.CANVAS_SESSION_MESSAGES,
        JSON.stringify({ chat_history: history }),
        "",
      );
      const { data: compressed } = compress(JSON.stringify(chunk));
      try {
        canvasWs.send(compressed);
      } catch {
        // Best-effort while the page is unloading.
      }
    };
    window.addEventListener("pagehide", handlePageHide);

    const handlePageShow = (event) => {
      if (!event.persisted) return;
      useUIStore.getState().commitLessonExit();
      window.location.reload();
    };
    window.addEventListener("pageshow", handlePageShow);

    const runUnmountCleanup = () => {
      const {
        clearCurrentSlideIndex,
        clearExtraListeningTimeout,
        clearResumeRuntimeState,
        clearSlides,
        setCurrentLessonId,
        setCurrentLessonName,
        setLessonId,
        setSessionId,
      } = unmountCleanupRef.current;
      debugLog("Clearing slide index and slides on unmount");

      // Stop session on component unmount (SPA navigation)
      stopLessonSession();

      clearCurrentSlideIndex();
      clearSlides();
      setLessonId(null);
      setCurrentLessonId(null);
      setCurrentLessonName(null);
      clearExtraListeningTimeout();
      clearResumeRuntimeState();
      // Clear sessionId to prevent websocket connection after unmount
      setSessionId(null);

      // Clean up pagehide and pageshow listeners to prevent memory leaks
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      // The unload that listener flagged is not happening in this page any
      // more; don't let it silence disconnects on the next route (Spaces).
      clearPageUnloading();

      // Reset notepad so it doesn't bleed into the next lesson
      useNotepadStore.getState().close();
      useNotepadPerSlideStore.getState().clear();
      // Clear completion UI before another lesson can mount.
      useUIStore.getState().closeLessonComplete();
      clearLessonStartupPerfAnchor();
      clearSessionStartupPerfAnchor();
    };

    return () => {
      runUnmountCleanup();
    };
  }, [unmountCleanupRef]);

  useEffect(() => {
    if (urlLessonId) {
      setCurrentLessonId(urlLessonId);
    } else if (lessonId) {
      setCurrentLessonId(lessonId);
    }
  }, [urlLessonId, lessonId, setCurrentLessonId]);

  useEffect(() => {
    // `hasResolvedSlideIndex` goes with the slides: entering a lesson makes the
    // retained `currentSlideIndex` stale, and readers must wait for discovery
    // to resolve it rather than act on the previous lesson's value.
    useSlideStore.setState({ slides: [], hasResolvedSlideIndex: false });
    useNotepadPerSlideStore.getState().clear();
    fetchSlidesByLesson(currentLessonId, mode);
  }, [currentLessonId, mode, fetchSlidesByLesson]);

  useEffect(() => {
    if (slides.length > 0) {
      useBiteStatusStore.getState().markAssessmentSlides(slides);
    }
  }, [slides]);

  const { user } = useAuthStore();
  const lessonStartupPerfOverride = useDebugStore(
    (state) => state.lessonStartupPerfOverride,
  );
  const lessonStartupPerfVariant = useMemo(
    () =>
      resolveLessonStartupPerfVariant(
        user?.config?.lessonStartupPerfVariant,
        lessonStartupPerfOverride,
      ),
    [lessonStartupPerfOverride, user?.config?.lessonStartupPerfVariant],
  );
  const lessonStartupOptimizationsEnabled =
    lessonStartupPerfVariant === "optimized";

  useEffect(() => {
    recordLessonStartupPerf("variant_selected", {
      lesson_id: currentLessonId,
      variant: lessonStartupPerfVariant,
      source:
        lessonStartupPerfOverride === "default"
          ? "user_config_or_default"
          : "debug_menu",
    });
  }, [currentLessonId, lessonStartupPerfOverride, lessonStartupPerfVariant]);

  useWebsocketClient();
  useNextSlideHtmlPrefetch(
    shouldInitializeSession &&
      lessonStartupOptimizationsEnabled &&
      startupNonCriticalReady,
    startupNonCriticalGateReason
      ? { startupGate: startupNonCriticalGateReason }
      : undefined,
  );

  const {
    startRecording,
    stopRecording,
    armRecording,
    abortArmedRecording,
    retryMicPermission,
    reinitForDeviceChange,
  } = useMicrophone({
    enabled: shouldInitializeSession,
    stopDebounceMs: user?.config?.speakbarStopDebounceMs,
  });

  const { addLog } = useLogger();
  const { cancelAllNotifications, showNotification } = useNotificationStore();

  const speakbarExtraListeningTimeout =
    user?.config?.speakbarExtraListeningTimeout ?? 300;

  const { ensurePcmPlayerActive } = useAudioPlayer();
  useTutorActionQueue();

  const handleCachedBiteChoiceWithClear = useCallback(
    (choice) => {
      clearQueue();
      onCachedBiteChoice(choice);
    },
    [clearQueue, onCachedBiteChoice],
  );

  const handleInterrupt = useCallback(
    ({ keepExitModal = false } = {}) => {
      debugLog("handleInterrupt");

      const { clearAllAnnotations } = useAnnotationStore.getState();
      clearAllAnnotations();
      cancelAllNotifications();
      if (!keepExitModal) {
        useUIStore.getState().setShowExitLessonModal(false);
      }
      if (window.pcmPlayer && window.pcmPlayer.audioCtx) {
        window.pcmPlayer.clear();
        debugLog("clearing inactivity timer");
        endInactivityTimer();
      }
      setIsAITalking(false);
      setTutorSessionStatus(TUTOR_SESSION_PHASE_STATUS.LISTENING);
      window.animationController?.call("SHRINK");

      setCurrentCaptions([]);
      setCaptionsFrozen(false);
      clearQueue();
    },
    [
      cancelAllNotifications,
      clearQueue,
      endInactivityTimer,
      setCurrentCaptions,
      setCaptionsFrozen,
      setIsAITalking,
      setTutorSessionStatus,
    ],
  );

  const handleNamePronunciationPause = useCallback(() => {
    window.pcmPlayer?.audioCtx?.suspend().catch(() => {});
    pauseTutorCaptions();
    setCurrentCaptions([]);
    setCaptionsFrozen(false);
    setIsNamePronunciationModalOpen(true);
  }, [
    pauseTutorCaptions,
    setCurrentCaptions,
    setCaptionsFrozen,
    setIsNamePronunciationModalOpen,
  ]);

  const handleNamePronunciationResume = useCallback(() => {
    setIsNamePronunciationModalOpen(false);
    window.pcmPlayer?.audioCtx?.resume().catch(() => {});
    resumeTutorCaptions();
  }, [resumeTutorCaptions, setIsNamePronunciationModalOpen]);

  useVoDemoRuntimeEvents({
    abruptPauseTimerRef,
    addLog,
    clearQueue,
    currentSessionId,
    doneChunkRecievedRef,
    emit,
    endInactivityTimer,
    handleChangeSlide,
    handleInterrupt,
    handleTutorAction,
    isCanvasOpenRef,
    isPausedRef,
    resetClock,
    resumeFragmentFloorRef,
    setCurrentCaptions,
    setIsAITalking,
    setIsAwaitingTutorResponseRef,
    setTutorSessionStatus,
    startInactivityTimer,
    startTimeRef,
    subscribe,
  });

  useVoDemoSessionBoot({
    currentLessonId,
    cachedBitePromptActive,
    formData,
    initializeCompletionData,
    lessonStartupPerfVariant,
    ltiLaunchRequestId,
    micPermissionDenied,
    mode,
    onRestartCleanup,
    onSessionBootComplete: handleSessionBootCompleteForStartup,
    onSessionEstablished,
    onSessionBootFailed,
    onSessionBootStarted,
    pendingRehydratePayload,
    restartRequestedRef,
    startChoice,
    startupAuxMetadataReady,
    setStartupAuxMetadataReady,
    searchParams,
    sessionStartNonce,
    setCurrentCaptions,
    setCurrentScreen,
    setCurrentSlideIndex,
    setSearchParams,
    setSessionId,
    setTurnId,
    shouldInitializeSession,
    tvOverrideRef,
    urlModel,
    urlVoiceModel,
  });

  useLessonAnalytics({
    currentLessonId,
    currentLessonName,
    currentSessionId,
    currentSlideIndex,
    enabled: startupNonCriticalReady,
    getSlideByIndex,
    slides,
  });

  const endLessonAfterAssessment = useCallback(
    () => handleLessonEnd(navigate, searchParams),
    [navigate, searchParams],
  );

  const advancePastAssessment = useCallback(() => {
    const targetIndex = getSlideIndexAfterAssessment(
      slides,
      useSlideStore.getState().currentSlideIndex,
    );
    if (targetIndex === null) {
      void endLessonAfterAssessment();
      return;
    }
    handleChangeSlide(currentSessionId || getSessionId(), targetIndex);
  }, [
    currentSessionId,
    endLessonAfterAssessment,
    getSessionId,
    handleChangeSlide,
    slides,
  ]);

  useAssessmentRuntime({
    activeAssessmentModal,
    activeAssessmentType: activeType,
    currentSlideIndex,
    getSlideByIndex,
    handleInterrupt,
    mode,
    onLessonEnd: endLessonAfterAssessment,
    setActiveAssessmentModal,
    setCurrentSlideIndex,
    shouldInitializeSession,
    slides,
  });

  const {
    isPressingToTalk,
    isArmingToTalk,
    isAwaitingTutorResponse,
    setIsAwaitingTutorResponse,
    hasPendingWork,
    submitPendingWork,
    extraListeningTimeoutRef,
    handleUnifiedButtonDown,
    handleUnifiedButtonUp,
  } = useSpeakButton({
    startRecording,
    stopRecording,
    armRecording,
    abortArmedRecording,
    retryMicPermission,
    cancelAllNotifications,
    emit,
    subscribe,
    handleInterrupt,
    setIsAITalking,
    setTutorSessionStatus,
    addLog,
    isAITalking,
    hasScratchpadPending,
    scratchpadCanvasExportRef,
    scratchpadImageItemsRef,
    scratchpadClearImageBytesRef,
    scratchpadRemoveImageByIdRef,
    pendingUploadedImageIdsByTurnRef,
    scratchpadResetPendingRef,
    markInputSubmitted,
    currentLessonId,
    speakbarExtraListeningTimeout,
    cachedBitePromptActive,
    onCachedBitePromptAttention: onCachedBiteAttention,
    hasCanvasViewportChanged,
    isCanvasOpen,
  });
  extraListeningTimeoutHolderRef.current = extraListeningTimeoutRef;
  setIsAwaitingTutorResponseRef.current = setIsAwaitingTutorResponse;

  useChatTrailSync();
  const sendChatMessage = useSendChatMessage({
    handleInterrupt,
    submitScratchpad: submitPendingWork,
  });

  // The value submitPendingWork itself gates on, so the chip, the send button
  // and the submission cannot disagree. It folds in input-box work and the
  // notepad's viewport case, which hasScratchpadPending alone misses.
  useEffect(() => {
    useScratchpadChipStore.getState().setHasWork(hasPendingWork);
  }, [hasPendingWork]);

  // Dev builds only: the submit Enter and the chip run, for agents. Every gate
  // (pending work, including ink the draw lock still holds, reply in flight,
  // blocked input, closed socket) stays inside submitPendingWork.
  useEffect(() => {
    if (!__DEV_OVERLAY__) return undefined;
    return provideDevCommand("tutor.submitWork", async () => {
      const isSubmitted = await submitPendingWork();
      return isSubmitted
        ? { ok: true }
        : {
            ok: false,
            error:
              "refused: no pending work, a reply in flight, blocked input or a closed socket",
          };
    });
  }, [submitPendingWork]);

  const handleScratchpadInteract = useCallback(() => {}, []);

  const {
    status: cameraStatus,
    countdown: cameraCountdown,
    autoCaptureCountdown: cameraAutoCaptureCountdown,
    detectionScore: cameraDetectionScore,
    debugValues: cameraDebugValues,
    lastCapturedImage: cameraLastCapturedImage,
    videoRef: cameraVideoRef,
    canvasRef: cameraCanvasRef,
    edgeCanvasRef: cameraEdgeCanvasRef,
    params: cameraParams,
    setParams: setCameraParams,
    open: openCamera,
    close: closeCamera,
    captureManually,
  } = usePaperCamera({ debugMode: SHOW_IMAGE_CAPTURE_DEBUG });

  // Surface camera permission / error states as non-blocking toasts.
  useEffect(() => {
    if (cameraStatus === "denied") {
      showNotification(
        "Camera access is needed to scan your paper",
        undefined,
        4000,
      );
    } else if (cameraStatus === "error") {
      showNotification(
        "Could not open camera — please try again",
        undefined,
        4000,
      );
    }
  }, [cameraStatus, showNotification]);

  usePracticeNotice(searchParams, showNotification);

  // Add image modal (scratchpad toolbar camera button)
  const [addImageModalOpen, setAddImageModalOpen] = useState(false);
  const [scratchpadImageCount, setScratchpadImageCount] = useState(0);
  const [pendingImageB64, setPendingImageB64] = useState(null);
  const [isSkippingToExitTicket, setIsSkippingToExitTicket] = useState(false);

  const handleScratchpadCameraClick = useCallback(() => {
    setAddImageModalOpen(true);
  }, []);

  // Called when user confirms in AddImageModal — stores b64 for scratchpad placing
  const handleImageConfirm = useCallback((b64) => {
    setPendingImageB64(b64);
  }, []);

  const handleImagePlaced = useCallback(() => {
    setPendingImageB64(null);
  }, []);

  return (
    <div
      className={`prevent-select flex bg-[#05070c] relative overflow-hidden w-full h-full ${isMobileLandscape ? "flex-row" : "flex-col"}`}
    >
      <SystemToasts
        onMicRetry={retryMicPermission}
        onPlaybackTap={() => ensurePcmPlayerActive("user_tap")}
      />
      <AnimatePresence>
        {showDisconnectedOverlay && <DisconnectedOverlay />}
      </AnimatePresence>

      {bufferingStatus && (
        <div className="absolute bottom-4 left-16 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
          Buffering audio...
        </div>
      )}

      <MicSettingsGearButton onDeviceConfirmed={reinitForDeviceChange} />

      {showNotepadCloseConfirm && (
        <NotepadCloseConfirmModal
          onConfirm={() => {
            setShowNotepadCloseConfirm(false);
            resumeTutorCaptions();
            resetAndRestartInactivityTimer();
            useNotepadStore.getState().close();
          }}
          onCancel={() => {
            setShowNotepadCloseConfirm(false);
            const player = window.pcmPlayer;
            if (player) {
              player.continue().finally(() => {
                resumeTutorCaptions();
                resetAndRestartInactivityTimer();
              });
            } else {
              resumeTutorCaptions();
              resetAndRestartInactivityTimer();
            }
          }}
        />
      )}

      {/* Main content area — takes all available space */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Screen
          mode={mode}
          isCanvasOpen={isCanvasOpen}
          canvasProps={
            isCanvasAvailable && canvasSessionId
              ? {
                  slideKey: `${canvasSessionId}-${currentSlideIndex}`,
                  canvasSessionId,
                  onStreamComplete: handleCanvasStreamComplete,
                  onError: handleCanvasError,
                  onActionExecuted: handleCanvasActionExecuted,
                  onRegisterHistoryGetter: handleRegisterCanvasHistoryGetter,
                  onClose: () => {
                    debugLog("[Canvas] Closing because user clicked close");
                    useNotepadStore.getState().close();
                  },
                  onViewportChanged: setHasCanvasViewportChanged,
                }
              : null
          }
          onScratchpadPendingChange={setHasScratchpadPending}
          scratchpadSubmitRef={scratchpadSubmitRef}
          scratchpadResetPendingRef={scratchpadResetPendingRef}
          scratchpadCanvasExportRef={scratchpadCanvasExportRef}
          scratchpadImageItemsRef={scratchpadImageItemsRef}
          scratchpadClearImageBytesRef={scratchpadClearImageBytesRef}
          scratchpadRemoveImageByIdRef={scratchpadRemoveImageByIdRef}
          onScratchpadInteract={handleScratchpadInteract}
          onCameraClick={handleScratchpadCameraClick}
          pendingImageB64={pendingImageB64}
          onImagePlaced={handleImagePlaced}
          onScratchpadImageCountChange={setScratchpadImageCount}
          onMicDeviceConfirmed={reinitForDeviceChange}
          onNamePronunciationPause={handleNamePronunciationPause}
          onNamePronunciationResume={handleNamePronunciationResume}
        />
      </div>

      <Captions />
      <CanvasCaptionsBridge />

      {/* Always-mounted camera canvas — must stay in DOM so usePaperCamera detection loop works */}
      <canvas ref={cameraCanvasRef} style={{ display: "none" }} />

      {/* Add image modal — triggered from scratchpad toolbar */}
      <AddImageModal
        open={addImageModalOpen}
        title="Add image to scratch pad"
        onClose={() => setAddImageModalOpen(false)}
        cameraStatus={cameraStatus}
        countdown={cameraCountdown}
        autoCaptureCountdown={cameraAutoCaptureCountdown}
        detectionScore={cameraDetectionScore}
        debugValues={cameraDebugValues}
        lastCapturedImage={cameraLastCapturedImage}
        videoRef={cameraVideoRef}
        edgeCanvasRef={cameraEdgeCanvasRef}
        params={cameraParams}
        setParams={setCameraParams}
        openCamera={openCamera}
        closeCamera={closeCamera}
        captureManually={captureManually}
        onConfirm={handleImageConfirm}
        debugMode={SHOW_IMAGE_CAPTURE_DEBUG}
        attachmentCount={scratchpadImageCount}
      />

      {/* BottomBar — in landscape becomes a right sidebar column */}
      <div
        className={isMobileLandscape ? "flex flex-col shrink-0" : "contents"}
      >
        <BottomBar
          onSendChat={sendChatMessage}
          isCanvasAvailable={isCanvasAvailable}
          isCanvasOpen={isCanvasOpen}
          notepadIngressType={notepadIngressType}
          notepadUsedThisSlide={notepadUsedThisSlide}
          currentScreen={currentScreen}
          mode={mode}
          currentSlideIndex={currentSlideIndex}
          slides={slides}
          onToggleNotepad={() => {
            // Notepad bite mode: notepad cannot be closed by user
            if (useNotepadStore.getState().isNotepadBite) return;
            if (isCanvasOpen) {
              if (
                notepadIngressType === "ai" &&
                notepadState !== "CLOSING_COUNTDOWN"
              ) {
                setShowNotepadCloseConfirm(true);
                endInactivityTimer();
                pauseTutorCaptions();
                window.pcmPlayer?.pause();
              } else {
                window.dispatchEvent(new CustomEvent("notepad:cancel-close"));
                useNotepadStore.getState().close();
              }
            } else {
              restorePagesFromSession();
              const { isLimitedMode, pages } = useNotepadStore.getState();
              if (isLimitedMode) {
                if (!notepadUsedThisSlide) return;
              } else {
                if (pages.length === 0) return;
                // Switch to the latest page before opening for review
                const latestPage = Math.max(...pages);
                useNotepadStore.getState().switchToPage(latestPage);
              }
              useNotepadStore.getState().openForReview();
            }
          }}
          isPressingToTalk={isPressingToTalk || isArmingToTalk}
          isAwaitingTutorResponse={isAwaitingTutorResponse}
          hasPendingWork={hasPendingWork}
          micUnavailable={micUnavailable}
          notepadState={notepadState}
          showKeyboardGuide={showKeyboardGuide}
          onPointerDown={handleUnifiedButtonDown}
          onPointerUp={handleUnifiedButtonUp}
          cachedBitePromptState={cachedBitePromptState}
          onCachedBiteChoice={handleCachedBiteChoiceWithClear}
          onCachedBiteAttention={onCachedBiteAttention}
          cachedBiteChoiceReady={shouldInitializeSession && wsConnected}
        />
      </div>

      {activeAssessmentModal && (
        <AssessmentModal
          type={activeAssessmentModal}
          onStart={async () => {
            const isCurrentLesson = captureLessonEndGuard();
            const modalType = activeAssessmentModal;
            setActiveAssessmentModal(null);
            const store = useAssessmentStore.getState();
            try {
              await store.fetchAssessment(
                modalType,
                Number(lessonId || urlLessonId),
                isCurrentLesson,
              );
              if (!isCurrentLesson()) return;
              await store.startAttemptRestoringResponses(
                modalType,
                isCurrentLesson,
              );
              if (!isCurrentLesson()) return;
              // Mount the survey only after savedResponses is set.
              store.setIsActive(modalType, true);
            } catch (err) {
              if (!isCurrentLesson()) return;
              console.error(`Failed to start ${modalType}:`, err);
              store.setIsActive(modalType, false);
            }
          }}
          {...(activeAssessmentModal === "exit_ticket"
            ? {}
            : {
                onSkip: () => {
                  useAssessmentStore
                    .getState()
                    .setPercentAnswered(activeAssessmentModal, 0);
                  setActiveAssessmentModal(null);
                  advancePastAssessment();
                },
              })}
        />
      )}

      {showExitLessonModal && (
        <ExitLessonModal
          onKeepGoing={() => setShowExitLessonModal(false)}
          {...((!assessments.exit_ticket?.has ||
            assessments.exit_ticket?.completed) && {
            onExit: async () => {
              const isCurrentLesson = captureLessonEndGuard();
              emit("LOCAL_INTERRUPT");
              setShowExitLessonModal(false);
              await captureAndUploadSlideScreenshot();
              if (!isCurrentLesson()) return;
              exitLesson(navigate, searchParams);
            },
          })}
          {...(assessments.exit_ticket?.has && {
            onSkipToExitTicket: async () => {
              const isCurrentLesson = captureLessonEndGuard();
              emit("LOCAL_INTERRUPT");
              setIsSkippingToExitTicket(true);
              try {
                await skipToExitTicket(
                  navigate,
                  searchParams,
                  Number(lessonId || urlLessonId),
                  isCurrentLesson,
                );
              } finally {
                if (isCurrentLesson()) {
                  setIsSkippingToExitTicket(false);
                  setShowExitLessonModal(false);
                }
              }
            },
            isSkippingToExitTicket,
          })}
        />
      )}

      {showLessonCompleteModal && (
        <LessonCompleteModal
          variant={lessonCompleteVariant ?? "quiet"}
          onExit={() => {
            closeLessonComplete();
            // Finished from Incomplete: the list says so once it shows the
            // lesson in Finished (Alloy, student v7).
            exitLesson(
              movedToFinished
                ? withMovedToFinished(navigate, Number(lessonId || urlLessonId))
                : navigate,
              searchParams,
            );
          }}
        />
      )}

      {activeType && (
        <AssessmentSurvey
          type={activeType}
          onComplete={() => {
            const t = activeType;
            useAssessmentStore.getState().setIsActive(t, false);
            if (t === "exit_ticket") {
              openLessonCompleteModal();
            } else {
              advancePastAssessment();
            }
          }}
          onSaveAndExit={() => {
            const t = activeType;
            useAssessmentStore.getState().setIsActive(t, false);
            if (t === "exit_ticket") {
              exitLesson(navigate, searchParams);
            } else {
              advancePastAssessment();
            }
          }}
        />
      )}

      {/* Custom keyboard for iPad/touch devices */}
      <CustomKeyboard
        inputPosition={activeInputId ? inputPositions[activeInputId] : null}
      />
      {debugEnabled && <DebugMenu />}
      {debugEnabled && (
        <ResumeDebugPanel pendingRehydratePayload={pendingRehydratePayload} />
      )}
    </div>
  );
}

export default VoDemo;
